import React, { useState, useEffect } from "react";
import HouseholdInfoForm from "../../../components/domains/financial-planning/retirement-planning/HouseholdInfoForm";
import AssetsForm from "../../../components/domains/financial-planning/retirement-planning/AssetsForm";
import FinancialAssumptionsForm from "../../../components/domains/financial-planning/retirement-planning/FinancialAssumptionsForm";
import DebtsForm from "../../../components/domains/financial-planning/retirement-planning/DebtsForm";
import ResultsDisplay from "../../../components/domains/financial-planning/retirement-planning/ResultsDisplay";

import logger from '../../../utils/logger';

const EfficientDistribution = () => {
  const [formData, setFormData] = useState({
    household: {
      firstMember: {
        name: "Scott",
        dateOfBirth: "1962-12-16",
        incomeSources: [
          {
            type: "salary",
            grossAmount: "8750",
            startDate: "2025-01-01",
            endDate: "2025-07-04",
            deductions: [
              {
                type: "federal_tax",
                amount: "1200",
                startDate: "2025-01-01",
                endDate: "2025-07-04",
              },
              {
                type: "401k",
                amount: "1700",
                startDate: "2025-01-01",
                endDate: "2025-07-04",
              },
              {
                type: "health_insurance",
                amount: "240",
                startDate: "2025-01-01",
                endDate: "2025-07-04",
              },
              {
                type: "state_tax",
                amount: "500",
                startDate: "2025-01-01",
                endDate: "2025-07-04",
              },
              {
                type: "retire_8",
                amount: "450",
                startDate: "2025-01-01",
                endDate: "2025-07-04",
              },
              {
                type: "social_security_tax",
                amount: "600",
                startDate: "2025-01-01",
                endDate: "2025-07-04",
              },
              {
                type: "medicare_tax",
                amount: "150",
                startDate: "2025-01-01",
                endDate: "2025-07-04",
              },
            ],
          },
          {
            type: "social_security",
            grossAmount: "2100",
            startDate: "2025-08-01",
            isLifetime: true,
            deductions: [
              {
                type: "federal_tax",
                amount: "210",
                startDate: "2025-08-01",
                isLifetime: true,
              },
              {
                type: "medicare_premium",
                amount: "400",
                startDate: "2028-01-01",
                isLifetime: true,
              },
            ],
          },
          {
            type: "pension",
            grossAmount: "2400",
            startDate: "2025-08-01",
            isLifetime: true,
            deductions: [
              {
                type: "federal_tax",
                amount: "240",
                startDate: "2025-08-01",
                isLifetime: true,
              },
              {
                type: "health_insurance",
                amount: "800",
                startDate: "2025-08-01",
                endDate: "2027-12-31",
              },
              {
                type: "vision",
                amount: "10",
                startDate: "2025-08-01",
                isLifetime: true,
              },
              {
                type: "dental",
                amount: "70",
                startDate: "2025-08-01",
                isLifetime: true,
              },
            ],
          },
        ],
      },
      isMarried: false,
      filingStatus: "single",
      taxState: "IL",
      secondMember: {
        name: "",
        dateOfBirth: "",
        incomeSources: [],
      },
    },
    assets: [
      {
        ownerType: "self",
        name: "Checking/Savings",
        financialInstitution: "Bank",
        assetType: "bank_account",
        taxQualification: "non_qualified",
        accountType: "checking",
        balance: "10000",
      },
      {
        ownerType: "self",
        name: "Schwab NQ",
        financialInstitution: "Schwab",
        assetType: "investment_account",
        taxQualification: "non_qualified",
        accountType: "individual",
        balance: "20000",
      },
      {
        ownerType: "self",
        name: "Stifel Traditional IRA",
        financialInstitution: "Stifel",
        assetType: "retirement_account",
        taxQualification: "tax_deferred",
        accountType: "ira",
        balance: "129000",
      },
      {
        ownerType: "self",
        name: "Stifel Roth IRA",
        financialInstitution: "Stifel",
        assetType: "retirement_account",
        taxQualification: "roth",
        accountType: "ira",
        balance: "53000",
      },
      {
        ownerType: "self",
        name: "457 Plan",
        financialInstitution: "457",
        assetType: "retirement_account",
        taxQualification: "tax_deferred",
        accountType: "401k",
        balance: "40000",
      },
    ],
    assumptions: {
      monthlyNetIncomeGoal: "4000",
      additionalIncomeGoals: [],
      inflationRate: 2.5,
      investmentReturn: 5.0,
      lifeExpectancy: {
        firstMember: 90,
        secondMember: 90,
      },
    },
    debts: [],
  });

  const [calculationResults, setCalculationResults] = useState(null);
  const [rmdTables, setRmdTables] = useState(null);
  const [taxData, setTaxData] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch("/data/rmdTables.json").then((res) => res.json()),
      fetch("/data/taxData.json").then((res) => res.json()),
    ])
      .then(([rmdData, taxData]) => {
        setRmdTables(rmdData);
        setTaxData(taxData);
      })
      .catch((error) => {
        logger.error("Error loading data:", error);
      });
  }, []);

  const handleFormChange = (section, data) => {
    setFormData((prev) => ({
      ...prev,
      [section]: data,
    }));
  };

  const calculateScenarios = () => {
    try {
      logger.info("Starting calculations with formData:", formData);

      // Calculate total assets by type with proper parsing
      const assetsByType = {
        non_qualified: 0,
        tax_deferred: 0,
        roth: 0,
      };

      // Sum up assets by their tax qualification
      formData.assets.forEach((asset) => {
        const balance = parseFloat(asset.balance) || 0;
        const taxType = asset.taxQualification;
        if (taxType in assetsByType) {
          assetsByType[taxType] += balance;
        }
      });

      // Calculate years to project more reliably
      const currentYear = new Date().getFullYear();
      const birthDate = new Date(formData.household.firstMember.dateOfBirth);
      const lifeExpectancy = parseInt(
        formData.assumptions.lifeExpectancy.firstMember,
      );
      const currentAge = calculateAge(
        formData.household.firstMember.dateOfBirth,
      );
      const yearsToProject = lifeExpectancy - currentAge;

      logger.info("Projection calculations:", {
        currentYear,
        birthYear: birthDate.getFullYear(),
        lifeExpectancy,
        currentAge,
        yearsToProject,
      });

      // Calculate projected incomes for each year
      const projectedIncomes = [];
      for (let i = 0; i < yearsToProject; i++) {
        const year = currentYear + i;
        let yearlyGross = 0;
        let yearlyNet = 0;

        // Calculate income for each source
        formData.household.firstMember.incomeSources.forEach((source) => {
          const startDate = new Date(source.startDate);
          const endDate = source.endDate ? new Date(source.endDate) : null;
          const currentDate = new Date(year, 0, 1);

          // Check if income source is active this year
          if (
            currentDate >= startDate &&
            (!endDate || currentDate <= endDate)
          ) {
            const monthlyGross = parseFloat(source.grossAmount) || 0;
            const yearlyGrossAmount = monthlyGross * 12;
            yearlyGross += yearlyGrossAmount;

            // Calculate deductions
            const yearlyDeductions = (source.deductions || []).reduce(
              (total, deduction) => {
                const deductionAmount = parseFloat(deduction.amount) || 0;
                return total + deductionAmount * 12;
              },
              0,
            );

            yearlyNet += yearlyGrossAmount - yearlyDeductions;
          }
        });

        projectedIncomes.push({
          year,
          gross: yearlyGross,
          net: yearlyNet,
        });
      }

      // Calculate base scenario with complete data
      const baseScenario = {
        totalRetirementIncome:
          parseFloat(formData.assumptions.monthlyNetIncomeGoal) * 12,
        totalAssets: Object.values(assetsByType).reduce(
          (sum, val) => sum + val,
          0,
        ),
        assetsByType: assetsByType,
        monthlyGrossIncome: 8750, // Starting salary
        monthlyIncome: 3910, // Net after deductions from salary
        monthlyExpenses: parseFloat(formData.assumptions.monthlyNetIncomeGoal),
        projectedIncomes: projectedIncomes,
        inflationRate: parseFloat(formData.assumptions.inflationRate),
        investmentReturn: parseFloat(formData.assumptions.investmentReturn),
        startingAge: currentAge,
        retirementAge: calculateRetirementAge(
          formData.household.firstMember.incomeSources,
        ),
        lifeExpectancy: lifeExpectancy,
        yearsToProject: yearsToProject,
      };

      // Create other scenarios with their own modifications
      const iraDistributionScenario = {
        ...baseScenario,
        assetsByType: { ...assetsByType },
      };

      const rothConversionScenario = {
        ...baseScenario,
        assetsByType: { ...assetsByType },
      };

      const results = {
        baseScenario,
        iraDistributionScenario,
        rothConversionScenario,
      };

      logger.info("Final results:", results);
      setCalculationResults(results);
    } catch (error) {
      logger.error("Error in calculateScenarios:", error);
      setCalculationResults({
        baseScenario: {
          totalRetirementIncome: 0,
          totalAssets: 0,
          assetsByType: {
            non_qualified: 0,
            tax_deferred: 0,
            roth: 0,
          },
          monthlyGrossIncome: 0,
          monthlyIncome: 0,
          monthlyExpenses: 0,
          projectedBalance: 0,
          startingAge: 40,
          error: error.message,
        },
        iraDistributionScenario: null,
        rothConversionScenario: null,
      });
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) {
      logger.info("No date of birth provided, using default age 40");
      return 40;
    }

    try {
      const today = new Date();
      const [year, month, day] = dateOfBirth
        .split("-")
        .map((num) => parseInt(num, 10));
      logger.info("Date components:", { year, month, day });

      if (!year || !month || !day) {
        logger.error("Invalid date components");
        return 40;
      }

      const birthDate = new Date(year, month - 1, day);
      logger.info("Birth date constructed:", birthDate);

      if (isNaN(birthDate.getTime())) {
        logger.error("Invalid birth date");
        return 40;
      }

      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      logger.info("Final calculated age:", age);
      return age;
    } catch (error) {
      logger.error("Error calculating age:", error);
      return 40;
    }
  };

  const calculateRetirementAge = (incomeSources) => {
    if (!incomeSources || incomeSources.length === 0) return 65; // default retirement age

    const endDates = incomeSources
      .filter((source) => source.endDate)
      .map((source) => new Date(source.endDate));

    if (endDates.length === 0) return 65;

    // Fix: Use Math.max with apply to handle array of dates
    const latestEndDate = new Date(Math.max.apply(null, endDates));

    const birthDate = new Date(formData.household.firstMember.dateOfBirth);
    const ageAtRetirement =
      latestEndDate.getFullYear() -
      birthDate.getFullYear() +
      (latestEndDate.getMonth() - birthDate.getMonth()) / 12;

    return Math.round(ageAtRetirement);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">
        Retirement Distribution Calculator
      </h1>

      <div className="grid grid-cols-1 gap-6">
        <HouseholdInfoForm
          data={formData.household}
          onChange={(data) => handleFormChange("household", data)}
        />

        <DebtsForm
          data={formData.debts}
          onChange={(data) => handleFormChange("debts", data)}
        />

        <AssetsForm
          data={formData.assets}
          onChange={(data) => handleFormChange("assets", data)}
        />

        <FinancialAssumptionsForm
          data={formData.assumptions}
          onChange={(data) => handleFormChange("assumptions", data)}
          isMarried={formData.household.isMarried}
        />

        <button
          className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 w-full md:w-auto"
          onClick={calculateScenarios}
        >
          Calculate Scenarios
        </button>

        {calculationResults && (
          <ResultsDisplay results={calculationResults} formData={formData} />
        )}
      </div>
    </div>
  );
};

export default EfficientDistribution;
